"use client";

import { createContext, type PropsWithChildren, useCallback, useContext } from "react";

import { useLocalStorage } from "@/hooks/storage/use-local-storage";
import { useAuthState } from "@/hooks/use-auth-state";
import { useSuiOwnerStorageMigration } from "@/hooks/use-sui-owner-storage-migration";
import { addressComparisonKey } from "@/utils/address";
import { DistributiveOmit } from "@/utils/types";

import { transferProgressStorageKey } from "./has-transfers";
import type { TransferProgressData, TransferStep } from "./transfer-progress-card";
import { useAutoImportTransfers } from "./use-auto-import-transfers";
import { useFunDepositTracker } from "./use-fun-deposit-tracker";
import { useReportConfirmedTransfers } from "./use-report-confirmed-transfers";
import { useTransferConfirmation } from "./use-transfer-confirmation";
import { useXchainDepositTracker } from "./use-xchain-deposit-tracker";
import { useXchainWithdrawTracker } from "./use-xchain-withdraw-tracker";
import { forgetDismissedDelivery } from "./xchain-imported-deposits";

interface TransferProgressContextValue {
  transfers: TransferProgressData[];
  addTransfer: (transfer: DistributiveOmit<TransferProgressData, "id">) => string;
  /** Atomically swap one transfer for another in a single localStorage write, preventing data loss
   *  if the browser closes between remove and add. The replacement keeps the old slot, so the pager
   *  stays on the same delivery; an unknown `oldId` appends instead. */
  replaceTransfer: (
    oldId: string,
    newTransfer: DistributiveOmit<TransferProgressData, "id">,
  ) => string;
  updateTransfer: (id: string, update: Partial<TransferProgressData>) => void;
  updateTransferStep: (id: string, step: TransferStep, timeRemaining?: string) => void;
  removeTransfer: (id: string) => void;
  clearAllTransfers: () => void;
}

const TransferProgressContext = createContext<TransferProgressContextValue | undefined>(undefined);

export function TransferProgressProvider({ children }: PropsWithChildren) {
  // An effect, so it lands *after* the first read below — a returning Sui user's history can read
  // empty for one render before the re-keyed entries arrive. That is a flash, not a loss: every
  // write the migration makes is a union (here and in the two imported-delivery ledgers), so a
  // tracker that reaches the new key first keeps its entries instead of being clobbered.
  useSuiOwnerStorageMigration();
  const { ownerWalletAddress } = useAuthState();
  // The key lives in `has-transfers` because the Toaster reads this same list to decide whether
  // to mount the progress card at all, and cannot import this module to get it.
  const [transfers, setTransfers] = useLocalStorage<TransferProgressData[]>(
    transferProgressStorageKey(ownerWalletAddress),
    [],
  );

  const addTransfer = useCallback(
    (transfer: DistributiveOmit<TransferProgressData, "id">): string => {
      const id = `transfer-${crypto.randomUUID()}`;
      const newTransfer = { ...transfer, id };
      let resultId = id;
      // Updater runs synchronously (useLocalStorage reads localStorage directly),
      // so resultId is assigned before the return below.
      setTransfers((prev) => {
        // Dedup on (userAddress, requestId) composite key to match WS/poll dedup,
        // preventing cross-wallet collisions in multi-wallet sessions.
        if ("requestId" in newTransfer && "userAddress" in newTransfer) {
          const nt = newTransfer as { requestId: string; userAddress: string };
          const ntUserKey = addressComparisonKey(nt.userAddress);
          const existing = prev.find(
            (t) =>
              "requestId" in t &&
              "userAddress" in t &&
              (t as { requestId: string; userAddress: string }).requestId === nt.requestId &&
              addressComparisonKey(
                (t as { requestId: string; userAddress: string }).userAddress,
              ) === ntUserKey,
          );
          if (existing) {
            resultId = existing.id;
            return prev;
          }
        }
        return [...prev, newTransfer];
      });
      return resultId;
    },
    [setTransfers],
  );

  const replaceTransfer = useCallback(
    (oldId: string, newTransfer: DistributiveOmit<TransferProgressData, "id">): string => {
      const id = `transfer-${crypto.randomUUID()}`;
      const entry = { ...newTransfer, id };
      setTransfers((prev) =>
        prev.some((t) => t.id === oldId)
          ? prev.map((t) => (t.id === oldId ? entry : t))
          : [...prev, entry],
      );
      return id;
    },
    [setTransfers],
  );

  const updateTransfer = useCallback(
    (id: string, update: Partial<TransferProgressData>) => {
      setTransfers((prev) =>
        prev.map((transfer) =>
          transfer.id === id ? ({ ...transfer, ...update } as TransferProgressData) : transfer,
        ),
      );
    },
    [setTransfers],
  );

  const updateTransferStep = useCallback(
    (id: string, step: TransferStep, timeRemaining?: string) => {
      setTransfers((prev) => {
        const updated = prev.map((transfer) => {
          if (transfer.id !== id) return transfer;

          const updatedTransfer = {
            ...transfer,
            currentStep: step,
            ...(timeRemaining !== undefined && { timeRemaining }),
          };

          return updatedTransfer;
        });

        return updated;
      });
    },
    [setTransfers],
  );

  const removeTransfer = useCallback(
    (id: string) => {
      // Releasing here rather than at the close button: every removal has to answer the question,
      // and a call site that forgets it is one that strands a delivery. Only a card stranded at a
      // step nothing transitions is released — see `forgetDismissedDelivery`, which the other
      // callers (the swept card, the mouse-leave on a finished one) fall straight through.
      const removed = transfers.find((transfer) => transfer.id === id);
      if (removed) forgetDismissedDelivery(removed, ownerWalletAddress);

      setTransfers((prev) => prev.filter((transfer) => transfer.id !== id));
    },
    [setTransfers, transfers, ownerWalletAddress],
  );

  const clearAllTransfers = useCallback(() => {
    setTransfers([]);
  }, [setTransfers]);

  return (
    <TransferProgressContext.Provider
      value={{
        transfers,
        addTransfer,
        replaceTransfer,
        updateTransfer,
        updateTransferStep,
        removeTransfer,
        clearAllTransfers,
      }}
    >
      <TransferConfirmationEffect />
      <AutoImportEffect />
      <ReportConfirmedTransfersEffect />
      <XchainDepositTrackerEffect />
      <FunDepositTrackerEffect />
      <XchainWithdrawTrackerEffect />
      {children}
    </TransferProgressContext.Provider>
  );
}

/** Runs txHash or balance polling for confirming transfers. Keeps running after user closes deposit modal. */
function TransferConfirmationEffect() {
  useTransferConfirmation();
  return null;
}

/** Scans for pending CCTP transfers on mount and imports them into the provider. */
function AutoImportEffect() {
  useAutoImportTransfers();
  return null;
}

/** POSTs each confirmed transfer to the warehouse once, for funding attribution. */
function ReportConfirmedTransfersEffect() {
  useReportConfirmedTransfers();
  return null;
}

/** Mirrors server-tracked NEAR Intents deposits into progress cards. */
function XchainDepositTrackerEffect() {
  useXchainDepositTracker();
  return null;
}

function FunDepositTrackerEffect() {
  useFunDepositTracker();
  return null;
}

/** The same for withdrawals, which also re-creates cards lost to a reload mid-flight. */
function XchainWithdrawTrackerEffect() {
  useXchainWithdrawTracker();
  return null;
}

export function useTransferProgress() {
  const context = useContext(TransferProgressContext);
  if (!context) {
    throw new Error("useTransferProgress must be used within a TransferProgressProvider");
  }
  return context;
}
